import React, { useEffect, useMemo, useRef, useState } from "react";

const LS_KEY = "a11y_prefs_v1";

const DEFAULT_PREFS = {
  textScale: 0, // 0 default, 1 medium, 2 large
  highContrast: false,
  underlineLinks: false,
  reduceMotion: false,
  focusHighlight: false,
};

function safeParseJSON(raw) {
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export default function AccessibilityWidget() {
  const panelId = useMemo(
    () => `a11y-panel-${Math.random().toString(36).slice(2)}`,
    []
  );

  const buttonRef = useRef(null);
  const panelRef = useRef(null);

  const [open, setOpen] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  // Load saved prefs
  useEffect(() => {
    try {
      if (!window || !window.localStorage) return;
      const raw = window.localStorage.getItem(LS_KEY);
      if (!raw) return;

      const parsed = safeParseJSON(raw);
      if (!parsed) return;

      setPrefs({
        textScale: [0, 1, 2].indexOf(parsed.textScale) !== -1 ? parsed.textScale : 0,
        highContrast: Boolean(parsed.highContrast),
        underlineLinks: Boolean(parsed.underlineLinks),
        reduceMotion: Boolean(parsed.reduceMotion),
        focusHighlight: Boolean(parsed.focusHighlight),
      });
    } catch {}
  }, []);

  // Persist + apply to <html>
  useEffect(() => {
    try {
      if (window && window.localStorage) {
        window.localStorage.setItem(LS_KEY, JSON.stringify(prefs));
      }
    } catch {}

    const root = document.documentElement;

    // text scale
    const fontSize =
      prefs.textScale === 1 ? "112.5%" : prefs.textScale === 2 ? "125%" : "";
    if (fontSize) root.style.fontSize = fontSize;
    else root.style.removeProperty("font-size");

    // class hooks (optional if you later add CSS)
    root.classList.toggle("a11y-contrast", prefs.highContrast);
    root.classList.toggle("a11y-underline-links", prefs.underlineLinks);
    root.classList.toggle("a11y-reduce-motion", prefs.reduceMotion);
    root.classList.toggle("a11y-focus", prefs.focusHighlight);
  }, [prefs]);

  // Close on outside click / escape
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") {
        setOpen(false);
        if (buttonRef.current) buttonRef.current.focus();
      }
    }

    function onClick(e) {
      if (!open) return;
      const panelEl = panelRef.current;
      const btnEl = buttonRef.current;

      if ((panelEl && panelEl.contains(e.target)) || (btnEl && btnEl.contains(e.target))) return;
      setOpen(false);
    }

    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return function cleanup() {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open]);

  function update(patch) {
    setPrefs(function (prev) {
      return Object.assign({}, prev, patch);
    });
  }

  const activeBtnStyle = {
    borderColor: "#0d9488",
    color: "#0d9488",
    fontWeight: 700,
  };

  return (
    <div style={{ position: "fixed", right: 16, bottom: 16, zIndex: 99999 }}>
      {/* Floating button */}
      <button
        ref={buttonRef}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((v) => !v)}
        style={{
          height: 42,
          width: 42,
          borderRadius: 9999,
          background: "#0a1176ff",
          color: "white",
          border: "1px solid rgba(0,0,0,0.08)",
          boxShadow: "0 10px 20px rgba(0,0,0,0.18)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
        }}
      >
        <span className="material-symbols-outlined">
          accessibility_new
        </span>


        <span style={srOnly}>Accessibility options</span>
      </button>

      {/* Panel */}
      {open && (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-label="Accessibility options"
          style={{
            marginTop: 12,
            width: 320,
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: 16,
            padding: 14,
            boxShadow: "0 18px 40px rgba(0,0,0,0.20)",
            color: "#0f172a",
          }}
        >
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div>
              <div style={{ fontSize: 16, fontWeight: 800 }}>Accessibility</div>
              <div style={{ marginTop: 4, fontSize: 12, color: "#475569" }}>
                Adjust display options for readability.
              </div>
            </div>

            <button
              type="button"
              onClick={() => {
                setOpen(false);
                if (buttonRef.current) buttonRef.current.focus();
              }}
              aria-label="Close accessibility options"
              style={{
                height: 36,
                width: 36,
                borderRadius: 10,
                border: "1px solid #e2e8f0",
                background: "white",
                cursor: "pointer",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>

          {/* Text size */}
          <div style={{ marginTop: 14, border: "1px solid #e2e8f0", borderRadius: 12, padding: 12 }}>
            <div style={{ fontSize: 13, fontWeight: 700 }}>Text size</div>

            <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
              {[0, 1, 2].map((size) => (
                <button
                  key={size}
                  type="button"
                  onClick={() => update({ textScale: size })}
                  style={{
                    flex: 1,
                    padding: "10px 10px",
                    borderRadius: 10,
                    border: "1px solid #e2e8f0",
                    background: "white",
                    cursor: "pointer",
                    ...(prefs.textScale === size ? activeBtnStyle : null),
                  }}
                >
                  {size === 0 ? "Default" : size === 1 ? "Medium" : "Large"}
                </button>
              ))}
            </div>
          </div>

          <Toggle label="High contrast" checked={prefs.highContrast} onChange={() => update({ highContrast: !prefs.highContrast })} />
          <Toggle label="Underline links" checked={prefs.underlineLinks} onChange={() => update({ underlineLinks: !prefs.underlineLinks })} />
          <Toggle label="Reduce motion" checked={prefs.reduceMotion} onChange={() => update({ reduceMotion: !prefs.reduceMotion })} />
          <Toggle label="Stronger focus outline" checked={prefs.focusHighlight} onChange={() => update({ focusHighlight: !prefs.focusHighlight })} />

          <button
            type="button"
            onClick={() => setPrefs(DEFAULT_PREFS)}
            style={{
              marginTop: 12,
              width: "100%",
              padding: "10px 12px",
              borderRadius: 10,
              border: "1px solid #e2e8f0",
              background: "white",
              cursor: "pointer",
              fontWeight: 800,
            }}
          >
            Reset
          </button>
        </div>
      )}
    </div>
  );
}

function Toggle({ label, checked, onChange }) {
  return (
    <button
      type="button"
      onClick={onChange}
      aria-pressed={checked}
      style={{
        marginTop: 10,
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: 12,
        border: "1px solid #e2e8f0",
        padding: "12px 12px",
        background: "white",
        cursor: "pointer",
      }}
    >
      <span style={{ fontSize: 13, fontWeight: 700 }}>{label}</span>

      <span
        aria-hidden="true"
        style={{
          height: 24,
          width: 44,
          borderRadius: 9999,
          background: checked ? "#0a2985ff" : "#cbd5e1",
          border: "1px solid rgba(0,0,0,0.10)",
          position: "relative",
          transition: "background 120ms ease",
        }}
      >
        <span
          style={{
            height: 20,
            width: 20,
            borderRadius: 9999,
            background: "white",
            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
            position: "absolute",
            top: 1,
            left: checked ? 22 : 2,
            transition: "left 120ms ease",
          }}
        />
      </span>
    </button>
  );
}

const srOnly = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0, 0, 0, 0)",
  whiteSpace: "nowrap",
  border: 0,
};
